import UrlService from 'common/services/Url';

/**
 * Champs étendus d'une place — `merchant.extended_data.extended_string_data_N`.
 *
 * ⚠️ La sémantique de ces 40 champs n'existe nulle part dans le code : le modèle
 * ne déclare que 40 chaînes libres (`models/definitions/place.js:209-249`) et le
 * marshaller les passe telles quelles (`places_manager.js:284`). Le nommage est
 * une donnée **par marque**, portée par `sponsor_info.extended_data_labels` et
 * éditable dans le back-office marque → Paramètres → « Champs étendus ». Aucun
 * seed du dépôt ne la fixe.
 *
 * Ce que le code du dépôt permet d'affirmer :
 *  - `_1` = Facebook et `_2` = Instagram sont unanimes sur les cinq templates,
 *    **y compris le TCHIP legacy en production**
 *    (`templates/TCHIP/src/views/LocalPage/widgets/FollowUs/FollowUs.js:8-20`).
 *    C'est la seule paire réellement corroborée pour cette marque.
 *  - La réservation / file d'attente est portée par `_3` chez VOG
 *    (`2025_VOG/.../Hairnet.js:6`), mais par **`_4`** chez le TCHIP legacy, avec
 *    le même libellé « Prendre place dans la file d'attente »
 *    (`templates/TCHIP/src/views/StoreLocator/MerchantList/MerchantList.js:87-88`,
 *    `templates/TCHIP/src/views/LocalPage/LocalPage.js:70` en `mergeField={4}`).
 *    **Tranché par Romain (03/09) : `_4` strict**, pas de repli sur `_3` — seul
 *    précédent qui concerne cette marque précise, un `_4` vide doit rester vide
 *    (§18 du plan) plutôt que de retomber silencieusement sur un autre champ.
 *
 *  - `_7` = description de repli du salon : VOG et le TCHIP legacy lisent tous
 *    les deux `merchant.description || extended_string_data_7`
 *    (`2025_VOG/.../Description.js:4-6`, `TCHIP/.../LocalPage.js:41`) — repris
 *    tel quel (§18 du plan), pas un choix propre à ce template.
 *
 *  - `_24` = discriminant de variante de galerie salon, propre à ce template
 *    (Romain, 03/09) : cas produit ponctuel pour permettre à un salon précis
 *    (TCHIP Miami) d'afficher une galerie différente sans étendre le contrat
 *    richfield. Voir `richfields/adapters.js` (`selectSalonSection`) pour la
 *    logique de sélection et la valeur attendue.
 *
 * Les champs `_8` et `_28` de la convention VOG (activation du widget avis,
 * affichage de l'iframe de réservation) ne sont lus par aucun composant de ce
 * template : ne pas les stuber, ça induit en erreur.
 */
export const EXTENDED_FIELDS = {
  FACEBOOK_URL: 1,
  INSTAGRAM_URL: 2,
  RESERVATION_URL_TCHIP: 4,
  DESCRIPTION_FALLBACK: 7,
  SALON_GALLERY_VARIANT: 24,
};

const read = (merchant, index) => {
  const data = (merchant && merchant.extended_data) || {};
  const value = data[`extended_string_data_${index}`];
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * Une URL saisie en back-office arrive souvent sans schéma
 * (« facebook.com/tchipcoiffure »). Telle quelle en `href`, elle est résolue en
 * **relatif** — `/{merchantId}/facebook.com/…`, soit un 404. `modifyHTTP` retire
 * le schéma éventuel et force `https://`.
 */
const absoluteUrl = (value) => (value ? UrlService.modifyHTTP(value) : '');

export const facebookUrl = (merchant) => absoluteUrl(read(merchant, EXTENDED_FIELDS.FACEBOOK_URL));

export const instagramUrl = (merchant) => absoluteUrl(read(merchant, EXTENDED_FIELDS.INSTAGRAM_URL));

/**
 * Deep link file d'attente du salon, ou `''` si le champ n'est pas renseigné —
 * comme le TCHIP legacy, pas de repli sur une URL générique (masquer le CTA
 * plutôt qu'afficher un lien qui ne correspond à aucun salon en particulier).
 */
export const reservationUrl = (merchant) => absoluteUrl(read(merchant, EXTENDED_FIELDS.RESERVATION_URL_TCHIP));

/**
 * Description éditoriale du salon, ou `''` si ni `merchant.description` ni le
 * champ étendu de repli ne sont renseignés — au consommateur de fournir son
 * propre filet (texte générique), comme pour les autres champs de ce module.
 */
export const salonDescription = (merchant) =>
  (merchant && merchant.description) || read(merchant, EXTENDED_FIELDS.DESCRIPTION_FALLBACK);

/**
 * Valeur brute du discriminant de galerie salon, non normalisée — la
 * comparaison (accents, casse, espaces) est de la responsabilité de l'appelant
 * (`richfields/adapters.js`, `selectSalonSection`), pas de cet accesseur.
 */
export const salonGalleryVariant = (merchant) => read(merchant, EXTENDED_FIELDS.SALON_GALLERY_VARIANT);

export default { facebookUrl, instagramUrl, reservationUrl, salonDescription, salonGalleryVariant };
